import { AbsoluteCenter, useToast } from '@chakra-ui/react';
import { motion } from "framer-motion";
import { t } from 'i18next';
import { isEmpty, isUndefined } from "lodash";
import { useEffect, useRef, useState } from "react";
import styled from 'styled-components';
import ToastMessageBox from '../../components/alerts/ToastMessageBox';
import SwipedBuddyPreference from '../../components/api/mutations/SwipedBuddyPreference';
import ToggleFollow from "../../components/api/mutations/ToggleFollow";
import useBuddyPreferenceQuery from "../../components/api/queries/useBuddyPreferenceQuery";
import useBuddyUserProfilesQuery from "../../components/api/queries/useBuddyUserProfilesQuery";
import RegularSolidButton from "../../components/buttons/RegularSolidButton";
import MutualMembersBottomDrawer from "../../components/drawer/MutualMembersBottomDrawer";
import EmptyCase from '../../components/errors/EmptyCase';
import { FanBuddyCardDialog } from "../../components/fan-buddy/FanBuddyCardDialog";
import FanBuddySwipeCard from "../../components/fan-buddy/FanBuddySwipeCard";
import { FollowedDialog } from "../../components/fan-buddy/FollowedDialog";
import InviteFanBuddySwipeCard from '../../components/fan-buddy/InviteFanBuddySwipeCard';
import StickyHeader from "../../components/StickyHeader";
import ProfileAvatar from "../../components/thumbnails/ProfileAvatar";
import LightColors from "../../constants/LightColors";
import { ReactComponent as ArrowReplayIcon } from "../../icons/arrow.reply.svg";
import { ReactComponent as ArrowTailIcon } from "../../icons/arrow.tail.svg";
import { openNewTab } from '../../utils/UIUtils';
import { isDarkMode } from '../../utils/ThemeHelper';

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 30px;
  padding: 12px 24px;
  align-items: center;
`

export default function FindFanBuddyCardPage() {
  const toast = useToast();
  const swipeCardRef = useRef()

  const [selectedBuddyUserProfile, setSelectedBuddyUserProfile] = useState();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [isShowMutualMemberBottomSheet, setIsShowMutualMemberBottomSheet] = useState(false);
  const [isShowFollowedDialog, setIsShowFollowedDialog] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [swipedUserProfileIds, setSwipedUserProfileIds] = useState(new Set());

  const currentUserBuddyPreference = useBuddyPreferenceQuery();

  const { data: profiles, fetchNextPage, hasNextPage } = useBuddyUserProfilesQuery();
  const buddyPreferences = profiles?.pages?.flatMap((page) =>
    page.buddyPreferences.map((buddyPreference) => buddyPreference)
  )

  useEffect(() => {
    if(currentIndex % 7 === 0 && hasNextPage) {
      fetchNextPage();
    }
  }, [currentIndex, hasNextPage, fetchNextPage])

  // 3초 후 로딩 화면 숨기기
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const buddyUserProfile = buddyPreferences?.[currentIndex]

  const hasPrevProfile = () => {
    return currentIndex !== 0
  }

  const hasNextProfile = () => {
    return (hasNextPage || (currentIndex + 1) < buddyPreferences?.length)
  }

  const presentRemainingCount = () => {
    return currentUserBuddyPreference.remainingSwipeCount - currentIndex > 0
  }

  const swipeCardAnimation = async (direction) => {
    if (swipeCardRef.current) {
      await swipeCardRef.current.triggerSwipeAnimation(direction);
    }
    if(direction === 'right') {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(currentIndex - 1);
    }
  }

  const handlePrev = async () => {
    if(!hasPrevProfile()) return;

    const newIndex = Math.max(currentIndex - 1, 0)
    if (newIndex !== currentIndex) {
      swipeCardAnimation('left');
    }
  }

  const handleNext = async () => {
    if (!hasNextProfile() || !presentRemainingCount()) {
      return;
    }

    const userProfileId = buddyUserProfile.userProfileId;

    // 이미 스와이프한 프로필인지 확인
    if (swipedUserProfileIds.has(userProfileId)) {
      // API 호출 없이 바로 다음으로 이동
      swipeCardAnimation('right');
      return;
    }

    const response = await SwipedBuddyPreference(userProfileId);
    if (response.ok) {
      // 스와이프한 프로필 ID 기록
      setSwipedUserProfileIds(prev => new Set(prev).add(userProfileId));

      swipeCardAnimation('right');
    } else {
      toast({
        duration: 1500,
        render: () => (
          <ToastMessageBox
            message={t('FindFanBuddyCardPage.nomoreSwipe')}
            showOnBottom={true}
          />
        )
      });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  const handleFollow = async () => {
    if (isEmpty(currentUserBuddyPreference) || isEmpty(buddyUserProfile)) {
      return;
    }

    const response = await ToggleFollow({
      isFollowed: false,
      currentUserProfileId: currentUserBuddyPreference.userProfileId,
      followUserProfileId: buddyUserProfile.userProfileId
    });

    if (response?.ok) {
      setIsShowFollowedDialog(true);
    }
  }

  const handleSelectedMutualMembers = (profile) => {
    setSelectedBuddyUserProfile(profile)
    setIsShowMutualMemberBottomSheet(true)
  }

  const navigateBuddyMatching = (buddyPreference) => {
    openNewTab(`/fan-buddy/matching?sourceUserProfileId=${currentUserBuddyPreference.userProfileId}&targetUserProfileId=${buddyPreference.userProfileId}`)
  }

  const navigateBuddyUserProfile = (buddyPreference) => {
    openNewTab(`/feed/user_profile/${buddyPreference.userProfileId}`)
  }

  const onClosedFollowedDialog = () => {
    handleNext()
  }

  const copyShareUrl = () => {
    try {
      navigator.clipboard.writeText("https://app.pocapan.com/fan-buddy/intro")
      toast({
        duration: 1500,
        render: () => (
          <ToastMessageBox message={t("chatRoom.copyMessageToClipboard")}/>
        ),
      })
    } catch (error) {
      console.log(error)
    }
  }

  if(isEmpty(currentUserBuddyPreference) || isUndefined(buddyPreferences)) {
    return (
      <></>
    )
  }

  return (
    <div
      className="safeAreaMarginBottom"
      style={{
        height: '100vh',
        background: `
          linear-gradient(
            180deg,
            #FFFFFF 0%,
            #FFFFFF 6%,
            rgba(255,255,255,0.3) 20%,
            #FFFFFF 32%,
            rgba(255,255,255,0.5) 69%,
            #FFFFFF 100%
          ),
          conic-gradient(
            from 0deg at 50% 50%,
            #A6F1FF 0%,
            #CFFFBD 30%,
            #FFBAD2 60%,
            #FFF3B2 90%,
            #A6F1FF 100%
          ),
          conic-gradient(
            from 0deg at 50% 50%,
            #266470 0%,
            #739866 30%,
            #A25772 60%,
            #968943 90%,
            #266470 100%
          )
        `,
        ...(isDarkMode()
          ? {
              background: `
                linear-gradient(
                  180deg,
                  #222222 0%,
                  #222222 6%,
                  rgba(34,34,34,0.3) 20%,
                  #222222 32%,
                  rgba(34,34,34,0.5) 69%,
                  #222222 100%
                ),
                conic-gradient(
                  from 0deg at 50% 50%,
                  #266470 0%,
                  #739866 30%,
                  #A25772 60%,
                  #968943 90%,
                  #266470 100%
                )
              `
            }
          : {})
      }}
    >
      <StickyHeader
        showBackButton={true}
        showTitle={true}
        alwaysShowTitle={true}
        title={t('FindFanBuddyCardPage.title')}
        transparent={true}
        rightContent={
          <ProfileAvatar
            profileImageUrl={currentUserBuddyPreference.thumbnailUrl}
            widthAndHeight={'29px'}
            svgWidthAndHeight={'18px'}
            onClick={() => setIsAlertOpen(true)}
          />
        }
      />
      <AbsoluteCenter
        style={{
          padding: '0px 24px 24px 24px',
          width: '100%',
          maxWidth: 'calc(var(--chakra-sizes-xl))'
        }}
      >
        {isInitialLoading ? (
          <div className="flex flex-col items-center justify-center relative max-h-[589px] min-h-[589px]">
            <div className="flex flex-col items-center">
              <img
                src="/assets/icons/fan-buddy/LoadingBubble.png"
                alt="Loading"
                className="w-[152px] h-[152px] mb-[19px]"
              />
              <div className="flex flex-col items-center gap-[14px] py-[10px]">
                <p className="text-[20px] leading-[24px] font-semibold text-main-light-1 dark:text-main-dark-1 text-center">
                  {t('FindFanBuddyCardPage.loadingTitle')}
                </p>
                <p className="text-[15px] font-medium text-main-light-4 dark:text-main-dark-4 text-center">
                  {t('FindFanBuddyCardPage.loadingDescription')}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {presentRemainingCount() ? (
              hasNextProfile() ? (
                <div className="flex flex-col items-center relative max-h-[589px] min-h-[589px]">
                  <FanBuddySwipeCard
                    ref={swipeCardRef}
                    isMine={false}
                    currentUserKeywords={currentUserBuddyPreference.keywords}
                    buddyUserProfile={buddyUserProfile}
                    onSelectedMatching={navigateBuddyMatching}
                    onSelectedMutualMembers={handleSelectedMutualMembers}
                    onSelectedBuddyUserProfile={navigateBuddyUserProfile}
                  />
                </div>
              ) : (
                <div>
                  <EmptyCase
                    emoji={'🥺'}
                    title={t('FindFanBuddyCardPage.emptyTitle')}
                    description={t('FindFanBuddyCardPage.emptyDescription')}
                    props={{ marginTop: '0px!important' }}
                  />
                </div>
              )
            ) : (
              <div className="flex flex-col items-center relative max-h-[589px] min-h-[589px]">
                <InviteFanBuddySwipeCard ref={swipeCardRef} />
              </div>
            )}
          </>
        )}
      </AbsoluteCenter>
      {/* Action Buttons */}
      {!isInitialLoading && !isEmpty(buddyPreferences) && (
        <motion.div
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <ActionButtonGroup
            className="bottomNavbar"
            style={{ marginBottom: 'calc(env(safe-area-inset-top))', background: 'transparent' }}
          >
            <button
              type="button"
              onClick={handlePrev}
              className={`
                w-12 h-12 rounded-full flex items-center justify-center
                bg-pure-white dark:bg-pure-black
                shadow-[0_2px_1px_0_rgba(0,0,0,0.05),0_4px_4px_0_rgba(0,0,0,0.10)]
                dark:shadow-[0_2px_1px_0_rgba(0,0,0,0.20),0_4px_4px_0_rgba(0,0,0,0.40)]
              `}
            >
              <ArrowReplayIcon
                width={24}
                height={24}
                fill={hasPrevProfile() ? LightColors.textmainlv2 : LightColors.disable}
              />
            </button>
            <RegularSolidButton
              disabled={!hasNextProfile() || !presentRemainingCount()}
              text={t('FindFanBuddyCardPage.followButton')}
              onClicked={handleFollow}
              className={`
                ${
                  !hasNextProfile() ||
                  (!presentRemainingCount() &&
                    'bg-disabled-light dark:bg-disabled-dark text-onDisabled-light dark:text-onDisabled-dark')
                }
              `}
              style={{
                flexGrow: 1,
                height: '52px',
                fontSize: '17px',
                color: 'white',
                background: 'linear-gradient(180deg, #5C3DFA 0%, #754EEB 100%)',
                boxShadow:
                  '0px 2px 1px 0px rgba(0, 0, 0, 0.05), 0px 4px 4px 0px rgba(0, 0, 0, 0.10)'
              }}
            />
            {/* 다음 덕메로 이동버튼부분 */}
            <div className="relative">
              <button
                type="button"
                onClick={handleNext}
                className={`
                  w-12 h-12 rounded-full flex items-center justify-center
                  bg-pure-white dark:bg-pure-black
                  shadow-[0_2px_1px_0_rgba(0,0,0,0.05),0_4px_4px_0_rgba(0,0,0,0.10)]
                  dark:shadow-[0_2px_1px_0_rgba(0,0,0,0.20),0_4px_4px_0_rgba(0,0,0,0.40)]
                `}
              >
                <ArrowTailIcon
                  width={24}
                  height={24}
                  fill={hasNextProfile() ? LightColors.primary : LightColors.disable}
                />
              </button>
              {hasNextProfile() && (
                <div
                  className="absolute rounded-full flex items-center justify-center"
                  style={{
                    top: -2,
                    right: -2,
                    width: '18px',
                    height: '18px',
                    background: 'linear-gradient(137.49deg, #FF5ACD 3.99%, #FF4C4C 100%)',
                    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <span className="text-white" style={{ fontSize: '13px' }}>
                    {Math.max(0, currentUserBuddyPreference.remainingSwipeCount - currentIndex)}
                  </span>
                </div>
              )}
            </div>
          </ActionButtonGroup>
        </motion.div>
      )}
      {isShowMutualMemberBottomSheet && (
        <MutualMembersBottomDrawer
          isOpen={isShowMutualMemberBottomSheet}
          onClose={() => setIsShowMutualMemberBottomSheet(false)}
          members={selectedBuddyUserProfile.mutualMembers}
        />
      )}
      {isAlertOpen && (
        <FanBuddyCardDialog
          isAlertOpen={isAlertOpen}
          isMine={true}
          setIsAlertOpen={setIsAlertOpen}
          buddyPreference={currentUserBuddyPreference}
        />
      )}
      {isShowFollowedDialog && (
        <FollowedDialog
          isAlertOpen={isShowFollowedDialog}
          isFollowed={selectedBuddyUserProfile?.isFollowing}
          setIsAlertOpen={setIsShowFollowedDialog}
          onClosed={onClosedFollowedDialog}
        />
      )}
    </div>
  );
}
